import { increment, reportFailure } from "./failureCounts.js";
import { noteStaleBundle } from "./staleBundle.js";
import { intentHeaders } from "./telemetry/intentEmitter.js";
import { validateWireResponse } from "./wireValidate.js";

/**
 * Thin fetch wrapper with centralized 401 handling.
 *
 * On 401: calls `onUnauthorized` (default: redirects to
 * /auth/login?returnTo=<current path+search>) and waits for navigation so
 * callers never see a partial result. An abort settles this wait as a failure.
 *
 * On all other responses: returns { ok, status, body } where body is the
 * parsed JSON or null if the body is not valid JSON.
 *
 * On network failure (fetch rejects, e.g. offline / DNS / CORS / abort):
 * returns { ok: false, status: 0, body: null }. Never throws.
 *
 * On a forecast-list GET (`/api/forecasts`, `/api/forecasts/shared`,
 * `/api/forecasts/by-contract`) whose 2xx body carries a row that does not
 * match the generated wire schema (lib/wireValidate.js, W-11): that ROW is
 * dropped from `body.forecasts` and reported — one
 * `console.error("forecasts.wire_shape_failed", …)` per dropped row, capped at
 * MAX_REPORTED_ROWS per response, and a session counter of the same name — while
 * every other row, and the rest of the envelope, hydrate as { ok, status, body }.
 * A read is never failed as a whole by a row, and a throw inside the validator
 * passes the body through unchanged after one
 * `console.error("forecasts.wire_validator_failed", …)`; still never throws.
 * Rows whose only fault is an UNKNOWN key (a field this bundle's schema
 * predates) pass through unchanged after one
 * `console.warn("apiClient wire_shape_extra", …)` (rulings R13/R14). A dropped row usually
 * means this tab's bundle predates a deploy that retyped a wire field, so the response that
 * drops rows also raises `noteStaleBundle("wire_shape")` (lib/staleBundle.js) — the guided
 * "new version — reload" banner, offered once — while the gate itself is unchanged.
 */

/** Dropped rows logged per response; the rest only count (the log is capped, the counter is not). */
const MAX_REPORTED_ROWS = 3;

/**
 * Redirect the browser to the login flow, preserving the current location as
 * returnTo. Pass `prompt: "select_account"` to force the Zitadel account
 * picker (the Switch-account affordance); omit it for an ordinary login, where
 * the server pins the request to the previously signed-in account. Centralized
 * here so the single `location.search` read is shared, not scattered.
 *
 * `replace: true` swaps the current history entry for the login page instead of
 * pushing one (`location.replace`, not `assign`): the automatic re-login on an
 * expired session must not leave the blank pre-auth shell in history, where Back
 * from the IdP would land on a page that only redirects again. The default
 * `assign` is for a user's own click (Sign in, Switch account), where Back should
 * return to the page they left.
 */
export function redirectToLogin({ prompt, replace = false } = {}) {
  const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
  const promptParam = prompt ? `&prompt=${encodeURIComponent(prompt)}` : "";
  const url = `/auth/login?returnTo=${returnTo}${promptParam}`;
  if (replace) window.location.replace(url);
  else window.location.assign(url);
}

// The automatic re-login on an expired session: `replace`, as AuthShells does, so Back from the
// IdP never lands on the blank shell this page became.
function defaultOnUnauthorized() {
  redirectToLogin({ replace: true });
}

/**
 * The request headers for `options`: its own `headers`, plus `x-intent-id` when the request
 * belongs to a user intent (`options.intentId`, P2 design §4.6: services/netCapture.js records
 * it on the `net` event, the server on the request's log lines).
 */
function headersOf(options, base = undefined) {
  const intent = intentHeaders(options?.intentId);
  if (!base && !options?.headers && !intent["x-intent-id"]) return undefined;
  return { ...(base || {}), ...(options?.headers || {}), ...intent };
}

async function parseBody(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Parse a Response body as JSON, returning null on parse failure.
 *
 * Use when the response status may be non-2xx with a non-JSON body — e.g.,
 * Cloud Run's `text/plain "Rate exceeded."` 429 leaks through as-is from
 * upstream throttling. Calling `.json()` directly throws a cryptic
 * `SyntaxError: Unexpected token 'R'`; this helper returns null instead so
 * callers can branch cleanly on `r.ok` and surface a meaningful error.
 *
 * Mirrors the internal `parseBody` used by `apiPost`/`apiGet`, exposed for
 * call sites in App.jsx that need direct fetch + headers access (forecast
 * + preprocess paths read x-forecaster-trace-id headers, so they can't go
 * through the body-only `apiPost` helper as-is).
 */
export async function parseJsonBodyOrNull(response) {
  return parseBody(response);
}

/**
 * Shared fetch/401/parse core for `apiPost`/`apiGet`. Runs `fetch(url,
 * fetchOpts)`, then applies the common response handling both wrappers need:
 * network failures never throw, a 401 fires `onUnauthorized` and waits until
 * aborted (contract: callers never see a partial result from a redirecting/
 * expiring session), and everything else resolves to
 * `{ok, status, body}` with the body JSON-parsed (or null on parse failure).
 *
 * @param {string} url
 * @param {RequestInit} fetchOpts — passed straight through to `fetch`.
 * `returnUnauthorized` hands a 401 back as `{ok:false, status:401}` instead, with no redirect,
 * for a caller that keeps unsaved input on screen and tells the user to sign in again.
 *
 * @param {{ onUnauthorized?: () => void, returnUnauthorized?: boolean, signal?: AbortSignal | null, headers?: Record<string, string>, intentId?: string | null }} [options]
 * @param {(response: Response) => Promise<unknown>} [parseOk] reads the body of a non-401
 *   answer; JSON (or null) unless the caller wants something else (`apiGetText`).
 * @returns {Promise<{ok: boolean, status: number, body: unknown}>}
 */
async function request(
  url,
  fetchOpts,
  { onUnauthorized = defaultOnUnauthorized, returnUnauthorized = false } = {},
  parseOk = parseBody,
) {
  let response;
  try {
    response = await fetch(url, fetchOpts);
  } catch {
    return { ok: false, status: 0, body: null };
  }

  if (response.status === 401 && returnUnauthorized) {
    return { ok: false, status: 401, body: await parseBody(response) };
  }
  if (response.status === 401) {
    // Swallow handler exceptions to honor the "never throws" contract — the
    // injectable seam means a caller/test double could throw, and the default
    // `window.location.assign` can throw under strict CSP / sandboxed contexts.
    try {
      onUnauthorized();
    } catch {
      /* contract: never throw */
    }
    // Navigation may be blocked. Once fetch has returned, its signal cannot
    // settle this wait for us; keep the caller's cancellation/deadline active.
    return new Promise((resolve) => {
      const abort = () => resolve({ ok: false, status: 0, body: null });
      if (fetchOpts.signal?.aborted) abort();
      else fetchOpts.signal?.addEventListener("abort", abort, { once: true });
    });
  }

  const parsed = await parseOk(response);
  return {
    ok: response.ok,
    status: response.status,
    body: applyWireGate(fetchOpts.method ?? "GET", url, response.status, parsed),
  };
}

/**
 * The wire-shape gate over a parsed response: returns the body `request` hands back.
 * `validateWireResponse` grades every row (only a 2xx GET on a forecast-list route with a
 * `forecasts` array is examined) and the verdict decides:
 *
 * - no row dropped: `parsed` itself, the same object (identity, no clone). Unknown keys only
 *   (R13/R14) are drift, observed once per response at WARN level — a tolerated row must not
 *   fail a user flow (nor an e2e, whose console trap fails on error).
 * - rows dropped: `{ ...parsed, forecasts: <the kept rows, in order> }` — every other envelope
 *   key (`pinned`, `view`, `next_page_token`) is preserved by the spread. Each dropped row up to
 *   MAX_REPORTED_ROWS is `reportFailure("forecasts.wire_shape_failed", …)`: one `console.error`
 *   whose message carries the url, the schema and the row's `/forecasts/<i>/<field> must …`
 *   errors; every further dropped row only `increment`s the same counter (rows dropped this
 *   session, exact), and the last reported line ends with the response's total so the cap hides
 *   nothing. The response then raises `noteStaleBundle("wire_shape")` (the guided reload,
 *   lib/staleBundle.js): a dropped row usually means a bundle older than the deployed row shape.
 * - the validator THREW (a validator bug — the generated validator does no runtime compile):
 *   that is the validator's failure, not the read's —
 *   `reportFailure("forecasts.wire_validator_failed", err)` once and `parsed` passes through
 *   unchanged. Never a dead read; the "never throws" contract holds whatever the validator does.
 */
function applyWireGate(method, url, status, parsed) {
  let verdict;
  try {
    verdict = validateWireResponse(method, url, status, parsed);
  } catch (err) {
    reportFailure("forecasts.wire_validator_failed", err);
    return parsed;
  }
  if (verdict.extra) {
    console.warn("apiClient wire_shape_extra", {
      url,
      schema: verdict.schema,
      extra: verdict.extra,
    });
  }
  const dropped = verdict.dropped;
  if (!dropped || dropped.length === 0) return parsed;
  const reported = Math.min(dropped.length, MAX_REPORTED_ROWS);
  for (let n = 0; n < dropped.length; n++) {
    if (n >= reported) {
      increment("forecasts.wire_shape_failed");
      continue;
    }
    const total =
      n === reported - 1 && dropped.length > reported ? ` (${dropped.length} rows dropped)` : "";
    reportFailure(
      "forecasts.wire_shape_failed",
      new Error(`${url} ${verdict.schema} ${dropped[n].errors.join("; ")}${total}`),
    );
  }
  noteStaleBundle("wire_shape");
  const droppedAt = new Set(dropped.map((row) => row.index));
  const envelope = /** @type {{ forecasts: unknown[] }} */ (parsed);
  return { ...envelope, forecasts: envelope.forecasts.filter((_, i) => !droppedAt.has(i)) };
}

/** The body as text on a 2xx, else as JSON (or null) like every other answer. */
async function parseTextBody(response) {
  if (!response.ok) return parseBody(response);
  try {
    return await response.text();
  } catch {
    return null;
  }
}

/**
 * POST a JSON body to `url`.
 *
 * @param {string} url
 * @param {unknown} [body] — will be JSON-stringified. Omit (or pass undefined)
 *   to send a bodyless POST (`apiPost(url)` sends no request body).
 * @param {{ onUnauthorized?: () => void, signal?: AbortSignal | null, headers?: Record<string, string>, intentId?: string | null }} [options]
 * @returns {Promise<{ok: boolean, status: number, body: unknown}>}
 */
export async function apiPost(url, body, options = {}) {
  return request(
    url,
    {
      method: "POST",
      credentials: "include",
      headers: headersOf(options, { "Content-Type": "application/json" }),
      body: JSON.stringify(body),
      // Optional AbortSignal (e.g. submit/editResubmit arm submitAbortRef so CLEAR can
      // cancel an in-flight preprocess). fetch ignores an undefined signal. On abort the
      // shared core's catch returns {ok:false,status:0}; callers distinguish abort from a
      // network failure via their own `signal.aborted` check.
      signal: options.signal,
    },
    options,
  );
}

/**
 * PATCH a JSON body to `url` (targeted per-resource edits, e.g.
 * `/api/forecasts/:id` prefs). Shares apiPost's `request` core, so the same
 * network-tolerance (fetch failures → {ok:false,status:0}), 401 handling, and
 * JSON-parse behaviour apply. As with apiPost, `body` is JSON-stringified inside
 * the helper, so it must be JSON-serialisable — a non-serialisable value (a
 * circular ref, a BigInt) throws from JSON.stringify, same as apiPost. Callers
 * treat it as fire-and-forget by ignoring the result.
 *
 * @param {string} url
 * @param {unknown} [body] — JSON-serialisable; JSON-stringified before send.
 * @param {{ onUnauthorized?: () => void, signal?: AbortSignal | null, headers?: Record<string, string>, intentId?: string | null }} [options]
 * @returns {Promise<{ok: boolean, status: number, body: unknown}>}
 */
export async function apiPatch(url, body, options = {}) {
  return request(
    url,
    {
      method: "PATCH",
      credentials: "include",
      headers: headersOf(options, { "Content-Type": "application/json" }),
      body: JSON.stringify(body),
      signal: options.signal,
    },
    options,
  );
}

/**
 * PUT a JSON body to `url` (whole-resource replace, e.g.
 * `/api/forecasts/:id/shares` — the share write is a whole-SET replace, not a
 * targeted edit, which is why this is PUT rather than apiPatch's PATCH).
 * Shares apiPost's `request` core, so the same network-tolerance (fetch
 * failures → {ok:false,status:0}), 401 handling, and JSON-parse behaviour
 * apply. As with apiPost/apiPatch, `body` is JSON-stringified inside the
 * helper, so it must be JSON-serialisable.
 *
 * @param {string} url
 * @param {unknown} [body] — JSON-serialisable; JSON-stringified before send.
 * @param {{ onUnauthorized?: () => void, signal?: AbortSignal | null, headers?: Record<string, string>, intentId?: string | null }} [options]
 * @returns {Promise<{ok: boolean, status: number, body: unknown}>}
 */
export async function apiPut(url, body, options = {}) {
  return request(
    url,
    {
      method: "PUT",
      credentials: "include",
      headers: headersOf(options, { "Content-Type": "application/json" }),
      body: JSON.stringify(body),
      signal: options.signal,
    },
    options,
  );
}

/**
 * DELETE `url` with credentials (e.g. the persistence save-effect's cascade delete of a
 * removed forecast/branch). Shares apiPost's `request` core, so the same network-tolerance,
 * 401 handling, and JSON-parse behaviour apply; callers typically treat it as fire-and-forget.
 *
 * @param {string} url
 * @param {{ onUnauthorized?: () => void, signal?: AbortSignal | null, headers?: Record<string, string>, intentId?: string | null }} [options]
 * @returns {Promise<{ok: boolean, status: number, body: unknown}>}
 */
export async function apiDelete(url, options = {}) {
  return request(
    url,
    {
      method: "DELETE",
      credentials: "include",
      headers: headersOf(options),
      signal: options.signal,
    },
    options,
  );
}

/**
 * GET `url` with credentials.
 *
 * @param {string} url
 * @param {{ onUnauthorized?: () => void, signal?: AbortSignal | null, headers?: Record<string, string>, intentId?: string | null }} [options]
 * @returns {Promise<{ok: boolean, status: number, body: unknown}>}
 */
export async function apiGet(url, options = {}) {
  return request(
    url,
    {
      method: "GET",
      credentials: "include",
      // Optional (e.g. the chat-turn header, lib/chatTurn.js); fetch ignores undefined.
      headers: headersOf(options),
      signal: options.signal,
    },
    options,
  );
}

/**
 * GET `url` with credentials, reading a 2xx body as text rather than JSON (a page, a file).
 * Everything else is `apiGet`'s: a non-2xx body is JSON-parsed (or null), a network failure is
 * `{ok:false, status:0, body:null}`, and a 401 is handled the same way.
 *
 * @param {string} url
 * @param {{ onUnauthorized?: () => void, signal?: AbortSignal | null, headers?: Record<string, string>, intentId?: string | null }} [options]
 * @returns {Promise<{ok: boolean, status: number, body: unknown}>} `body` is a string on a 2xx
 */
export async function apiGetText(url, options = {}) {
  return request(
    url,
    {
      method: "GET",
      credentials: "include",
      headers: headersOf(options),
      signal: options.signal,
    },
    options,
    parseTextBody,
  );
}
